import type { MouseEvent, ReactElement } from 'react'
import { Link } from 'react-router-dom'

import { cn } from '@/lib/utils'
import { PRODUCT_ICON } from '@/packages/explore/components/product-icons'
import { useLocalize } from '@/packages/explore/lib/localize'
import { opensInNewTab } from '@/packages/explore/lib/opensInNewTab'
import type { Product } from '@/packages/explore/types'

interface ProductTileProps {
  product: Product
  /** `highlighted` is the red `Highlighted` tile above the groups. */
  variant?: 'highlighted' | 'default'
  onNavigate?: (() => void) | undefined
  nodeId?: string
}

/**
 * Figma `Highlighted` (2267:290738) / `product` (2267:290766) — a 44px icon
 * tile over a centred 12/16 label, 6px apart.
 *
 * The tile is 44px because Figma pads a 20px glyph by 12px; the stroke is an
 * inset ring so it does not grow the box.
 */
export function ProductTile({
  product,
  variant = 'default',
  onNavigate,
  nodeId,
}: ProductTileProps): ReactElement {
  const localize = useLocalize()
  const Glyph = product.icon ? PRODUCT_ICON[product.icon] : undefined

  const externalUrl = product.href ? localize(product.href) : null

  const dataName = variant === 'highlighted' ? 'Highlighted' : 'product'
  const wrapperClass = 'group flex w-full flex-col items-center gap-1.5'

  const innerContent = (
    <>
      <span
        data-name="button-lg"
        className={cn(
          'flex size-11 shrink-0 items-center justify-center rounded-md p-3 transition-[filter]',
          'group-hover:brightness-95',
          variant === 'highlighted' ? 'hub-tile-accent text-avatar-text' : 'hub-tile text-icon',
        )}
      >
        {Glyph && <Glyph className="size-5" aria-hidden="true" />}
      </span>

      <span className="hub-meta-md text-widget-secondary w-full text-center">
        {localize(product.label)}
      </span>
    </>
  )

  if (externalUrl) {
    return (
      <a
        href={externalUrl}
        target="_blank"
        rel="noopener noreferrer"
        data-node-id={nodeId}
        data-name={dataName}
        className={wrapperClass}
      >
        {innerContent}
      </a>
    )
  }

  const handleClick = (event: MouseEvent<HTMLAnchorElement>): void => {
    if (!opensInNewTab(event)) onNavigate?.()
  }

  return (
    <Link
      to={product.to || '#'}
      onClick={handleClick}
      data-node-id={nodeId}
      data-name={dataName}
      className={wrapperClass}
    >
      {innerContent}
    </Link>
  )
}
